import { clsx } from 'clsx'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { type DomainSummary, useDeleteDomain, useDomains } from '@/api/hooks/use-domains'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAuth } from '@/lib/auth-context'
import { formatRelativeTime } from '@/lib/format'
import { AddDomainModal } from '@/pages/domains/add-domain-modal'
import { DomainDetailPanel } from '@/pages/domains/domain-detail-panel'

function DomainRow({ domain }: { domain: DomainSummary }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const deleteDomain = useDeleteDomain()

  return (
    <div className="border-b border-surface-3 last:border-b-0">
      {/* biome-ignore lint/a11y/useSemanticElements: expandable row with complex inner content */}
      <div
        role="button"
        tabIndex={0}
        className="flex cursor-pointer items-center justify-between px-6 py-4 hover:bg-surface-1"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded(!expanded)
          }
        }}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-text-tertiary" />
          ) : (
            <ChevronRight className="h-4 w-4 text-text-tertiary" />
          )}
          <div>
            <p className="font-medium text-text-primary">{domain.domain}</p>
            <p className="text-xs text-text-tertiary">
              Added {formatRelativeTime(domain.created_at)}
              <span className="ml-2">
                {domain.verified_records}/{domain.total_records} records verified
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge status={domain.status} />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (window.confirm(`Delete domain ${domain.domain}?`)) {
                deleteDomain.mutate(domain.id)
              }
            }}
            className="rounded-lg p-2 text-text-secondary hover:bg-red-50 hover:text-danger"
            title="Delete domain"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-6">
          <DomainDetailPanel
            domainId={domain.id}
            domainName={domain.domain}
            status={domain.status}
          />
        </div>
      )}
    </div>
  )
}

const TIER_DOMAIN_LIMITS: Record<string, number> = {
  free: 1,
  starter: 3,
  pro: 10,
  growth: 25,
  scale: 100,
}

export function DomainListPage(): React.JSX.Element {
  const [showAddModal, setShowAddModal] = useState(false)
  const { data, isLoading } = useDomains()
  const { account } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  const domains = data?.data ?? []
  const tier = account?.tier ?? 'free'
  const domainLimit = TIER_DOMAIN_LIMITS[tier] ?? 1
  const atLimit = domains.length >= domainLimit

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">Domains</h1>
        <div className="flex items-center gap-3">
          {atLimit && (
            <span className="text-xs text-text-tertiary">
              {domains.length}/{domainLimit} domains ({tier} plan)
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              if (atLimit) {
                toast.error(
                  `Your ${tier} plan allows ${domainLimit} domain${domainLimit > 1 ? 's' : ''}. Upgrade to add more.`,
                )
                return
              }
              setShowAddModal(true)
            }}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium',
              atLimit
                ? 'bg-surface-2 text-text-tertiary cursor-not-allowed'
                : 'bg-brand-500 text-white hover:bg-brand-600',
            )}
          >
            <Plus className="h-4 w-4" />
            {atLimit ? 'Upgrade to add more' : 'Add Domain'}
          </button>
        </div>
      </div>

      {domains.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
          <p className="text-lg font-medium text-text-primary">No domains yet</p>
          <p className="mt-1 text-sm text-text-secondary">Add a domain to start sending emails.</p>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            <Plus className="h-4 w-4" />
            Add Domain
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
          {domains.map((domain) => (
            <DomainRow key={domain.id} domain={domain} />
          ))}
        </div>
      )}

      <AddDomainModal open={showAddModal} onClose={() => setShowAddModal(false)} />
    </div>
  )
}
